import React, { useEffect, useMemo, useState } from 'react';
import {
  ErrorHandler,
  InfoTooltip,
  PageSteps,
  TableTransfer,
} from '@common/components';
import { setBreadcrumbs } from 'works/legacy/lib/client-store';
import { useMutation, useQuery, useNavigate, useParams } from '@common/hooks';
import { filterableColumnBuilder } from 'works/legacy/lib/table-filters-helper';
import { Tooltip } from 'antd';
import { ComplianceLinkAssignmentsSummary } from '@onboarding/pages/compliance-link-assignments-steps/compliance-link-assignments-summary';
import { ComplianceLinkAssignmentsWarningModal } from '@onboarding/pages/compliance-link-assignments-steps/compliance-link-assignments-warning-modal';
import {
  AssignmentState,
  ComplianceAssignmentFragmentType,
  ComplianceAssignmentsQuery,
  ComplianceAssignmentsQueryArgs,
  ComplianceAssignmentsQueryResponse,
  RelinkWorksListTemplateArgs,
  RelinkWorksListTemplateQuery,
  RelinkWorksListTemplateResponse,
} from '@onboarding/graphql';
import { createMessage } from 'works/legacy/lib/message-helper';
import { Colors } from 'works/legacy/lib/style-helper';
import { ORGANIZATION_ADMIN_MENU } from 'works/pages/organization/admin-settings/organization-admin-vertical-nav-menu';

type AssignmentDataSource = ComplianceAssignmentFragmentType & {
  key: string;
  disabled?: boolean;
};

export function ComplianceLinkAssignments() {
  const { id: worksListTemplateId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [targetKeys, setTargetKeys] = useState<string[]>([]);
  const [dataSource, setDataSource] = useState<AssignmentDataSource[]>([]);
  const [warningModalVisible, setWarningModalVisible] = useState<boolean>(true);

  function goToWorksListTemplateRoute() {
    navigate(
      `/works/admin/templates/compliance-list/${worksListTemplateId}/items`
    );
  }

  function closeWarningModal() {
    setWarningModalVisible(false);
  }

  function assignmentAssociatedToCurrentTemplate(
    assignment: ComplianceAssignmentFragmentType
  ) {
    return assignment?.worksList?.worksListTemplate?.id === worksListTemplateId;
  }

  function excludeUnlinkableAssignments(
    assignments: ComplianceAssignmentFragmentType[]
  ) {
    return assignments.filter((assignment) => {
      return !(
        !assignmentAssociatedToCurrentTemplate(assignment) &&
        unlinkableStates.includes(assignment.state)
      );
    });
  }

  function normalizeRows(
    data: ComplianceAssignmentFragmentType[]
  ): AssignmentDataSource[] {
    return excludeUnlinkableAssignments(data).map((assignment) => ({
      key: assignment.id,
      disabled: assignmentAssociatedToCurrentTemplate(assignment),
      ...assignment,
    }));
  }

  function alreadyLinkedAssignmentKeys(assignments: AssignmentDataSource[]) {
    return assignments
      .filter(assignmentAssociatedToCurrentTemplate)
      .map((assignment) => assignment.key);
  }

  const { loading, error, data } = useQuery<
    ComplianceAssignmentsQueryResponse,
    ComplianceAssignmentsQueryArgs
  >(ComplianceAssignmentsQuery, {
    variables: {
      id: worksListTemplateId,
      state: ['cleared', 'onboarding', ...unlinkableStates],
    },
    onCompleted(data) {
      const assignments = normalizeRows(data?.staffingRequestAssignments || []);
      const targetKeys = alreadyLinkedAssignmentKeys(assignments);

      setDataSource(assignments);
      setTargetKeys(targetKeys);
    },
  });

  const assignmentsToBeLinked = useMemo(() => {
    return dataSource.filter(
      ({ key, disabled }) => !disabled && targetKeys.includes(key)
    );
  }, [dataSource, targetKeys]);

  const [relink, { loading: relinkLoading }] = useMutation<
    RelinkWorksListTemplateResponse,
    RelinkWorksListTemplateArgs
  >(RelinkWorksListTemplateQuery, {
    variables: {
      worksListTemplateId: worksListTemplateId,
      staffingRequestAssignmentIds: assignmentsToBeLinked.map(getKey),
    },
    onCompleted() {
      createMessage('success', 'Assignments were successfully updated.');
      goToWorksListTemplateRoute();
    },
    onError() {
      createMessage(
        'error',
        'There was an error while updating. Please try again or contact support.'
      );
    },
  });

  const worksListTemplateName = data?.worksListTemplate?.name;

  useEffect(
    () =>
      setBreadcrumbs([
        {
          name: 'Admin',
          path: '/works/admin',
        },
        {
          name: 'Templates',
          path: '/works/admin/templates',
        },
        {
          name: ORGANIZATION_ADMIN_MENU.TEMPLATES__COMPLIANCE.LABEL,
          path: ORGANIZATION_ADMIN_MENU.TEMPLATES__COMPLIANCE.ROUTE,
        },
        {
          name: worksListTemplateName,
          path: `${ORGANIZATION_ADMIN_MENU.TEMPLATES__COMPLIANCE.ROUTE}/${worksListTemplateId}/items`,
        },
        {
          name: 'Link Assignments',
          path: `${ORGANIZATION_ADMIN_MENU.TEMPLATES__COMPLIANCE.ROUTE}/${worksListTemplateId}/link-assignments`,
        },
      ]),
    [worksListTemplateId, worksListTemplateName]
  );

  const hasAssignmentsSelected = useMemo(() => {
    return dataSource
      .filter((assignment) => !assignment.disabled)
      .some(({ key }) => targetKeys.includes(key));
  }, [dataSource, targetKeys]);

  if (error) {
    return <ErrorHandler error={error} log="compliance-link-assignments" />;
  }

  function handleChange(nextTargetKeys: string[]) {
    setTargetKeys(nextTargetKeys);
  }

  return (
    <>
      <PageSteps
        steps={[
          {
            title: 'Select Assignments',
            page: (
              <TableTransfer
                showSearch
                loading={loading}
                dataSource={dataSource}
                targetKeys={targetKeys}
                columns={tableColumns}
                onChange={handleChange}
                titles={transferTitles}
              />
            ),
            forwardButtonDisabled: loading || !hasAssignmentsSelected,
          },
          {
            title: 'Summary',
            page: (
              <ComplianceLinkAssignmentsSummary
                worksListTemplateName={worksListTemplateName || ''}
                assignmentsToBeLinked={assignmentsToBeLinked}
              />
            ),
          },
        ]}
        onConfirm={relink}
        confirmLoading={relinkLoading}
        onCancel={goToWorksListTemplateRoute}
      />
      <ComplianceLinkAssignmentsWarningModal
        visible={warningModalVisible}
        onContinue={closeWarningModal}
      />
    </>
  );
}

const column = filterableColumnBuilder<AssignmentDataSource>({});

const tableColumns = [
  column({
    key: 'worksProfile',
    title: 'Name',
    dataIndex: ['worksProfile', 'fullName'],
    render: (value, record) => {
      return record.disabled ? (
        <Tooltip title="Already linked">
          <div>{value}</div>
        </Tooltip>
      ) : (
        value
      );
    },
  }),
  column({
    key: 'supplier',
    dataIndex: ['supplier', 'name'],
    title: 'Supplier',
  }),
  column({
    key: 'worksList',
    dataIndex: ['worksList', 'name'],
    title: 'Compliance List',
    render: (value) => value || 'Unlinked Compliance List',
  }),
  column({
    key: 'organizationUnit',
    dataIndex: ['organizationUnit', 'name'],
    title: 'Unit',
  }),
];

function getKey(assignment: AssignmentDataSource) {
  return assignment.key;
}

const unlinkableStates: AssignmentState[] = ['working'];

const transferTitles = {
  left: (
    <>
      Onboarding Assignments Available
      <InfoTooltip
        leftOffset={6}
        iconColor={Colors.elm}
        title="Select onboarding assignments below and move to the right to relink to this compliance template."
      />
    </>
  ),
  right: <>Assignments Already Linked or To Be Linked</>,
};
